import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Check, X } from 'lucide-react';
import React from 'react';

import type { TabOption } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Tooltip } from '@/components/Tooltip';
import type {
  MainTabKey,
  NormalizedSecretFinding,
  POEContent,
  SecretsPOE,
  SectionContent,
} from '@/sections/poe/types/poe.types';
import { safeExecute } from '@/utils/function.util';

// Module-private helper (also in POE.tsx until Phase 3d cleans up the other caller)
const formatTimestamp = (timestamp: string | undefined): string => {
  if (!timestamp) return 'Unknown';

  try {
    // Handle Unix timestamp with timezone (e.g., "1407370977 -0700")
    const parts = timestamp.split(' ');
    const unixTime = parseInt(parts[0]) * 1000;
    const date = new Date(unixTime);

    if (parts[1]) {
      return `${date.toLocaleString()} (${parts[1]})`;
    }

    return date.toLocaleString();
  } catch {
    return timestamp;
  }
};

const normalizeSecretsFindings = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
): NormalizedSecretFinding[] | null => {
  if (!data) return null;

  // Single NPFinding format (AWS/Azure/GCP cloud secrets)
  if (data.finding_id && data.provenance && !data.matches) {
    return [
      {
        finding_id: data.finding_id,
        rule_name: data.rule_name,
        rule_text_id: data.rule_text_id,
        provenance: {
          kind: data.provenance.kind,
          platform: data.provenance.platform,
          resource_type: data.provenance.resource_type,
          resource_id: data.provenance.resource_id,
          region: data.provenance.region,
          account_id: data.provenance.account_id,
        },
        snippet: data.snippet,
        validation: data.validation,
      },
    ];
  }

  // Repo secrets format with matches array (Nosey Parker)
  if (data.matches && Array.isArray(data.matches)) {
    const findings: NormalizedSecretFinding[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.matches.forEach((match: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (match.provenance || []).forEach((prov: any) => {
        findings.push({
          finding_id: data.finding_id,
          rule_name: data.rule_name,
          rule_text_id: data.rule_text_id,
          provenance: {
            kind: prov.kind,
            platform: prov.platform,
            resource_type: prov.resource_type,
            resource_id: prov.resource_id,
            region: prov.region,
            account_id: prov.account_id,
            repo_path: prov.repo_path,
            first_commit: prov.first_commit,
          },
          snippet: match.snippet,
          location: match.location,
          validation: data.validation,
        });
      });
    });
    return findings.length > 0 ? findings : null;
  }

  return null;
};

// Helper: Get location key for grouping (cloud resource ID or file path)
const getLocationKey = (finding: NormalizedSecretFinding): string => {
  // Cloud resource: use resource ID
  if (finding.provenance.resource_id) {
    return finding.provenance.resource_id;
  }
  // Git repo: use file path
  if (finding.provenance.first_commit?.blob_path) {
    return finding.provenance.first_commit.blob_path;
  }
  return 'Unknown';
};

// Helper: Get display name for detection tab
const getSecretDisplayName = (finding: NormalizedSecretFinding): string => {
  // Cloud resource: use resource type + short ID
  if (finding.provenance.resource_type && finding.provenance.resource_id) {
    const shortId =
      finding.provenance.resource_id.split(':').pop()?.split('/').pop() ||
      'resource';
    return `${finding.provenance.resource_type}: ${shortId}`;
  }
  // Git repo: use filename:line
  if (finding.provenance.first_commit?.blob_path) {
    const filePath = finding.provenance.first_commit.blob_path;
    const fileName = filePath.split('/').pop() || 'Unknown';
    const line = finding.location?.source_span?.start?.line || 0;
    return `${fileName}:${line}`;
  }
  return finding.rule_name;
};

// Helper: Get location display for detection tab content
const getLocationDisplay = (finding: NormalizedSecretFinding): string => {
  if (finding.provenance.resource_id) {
    return (
      finding.provenance.resource_id.split(':').pop()?.split('/').pop() ||
      finding.provenance.resource_id
    );
  }
  if (finding.provenance.first_commit?.blob_path) {
    return (
      finding.provenance.first_commit.blob_path.split('/').pop() || 'Unknown'
    );
  }
  return 'Unknown';
};

export const addDetectionRuleSection = (
  ruleName: string | undefined,
  ruleId: string | undefined,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>
) => {
  if (!ruleName) return;

  sections['detection_rule'] = (
    <div className="space-y-2">
      <div className="text-sm">
        <span className="text-chariot-brand-primary font-medium">
          Rule Name:
        </span>
        <span className="text-chariot-text-primary ml-2">{ruleName}</span>
      </div>
      {ruleId && (
        <div className="text-sm">
          <span className="text-chariot-brand-primary font-medium">
            Rule ID:
          </span>
          <span className="text-chariot-text-secondary ml-2 font-mono text-xs">
            {ruleId}
          </span>
        </div>
      )}
    </div>
  );

  tempTabs['INFORMATION'].push({
    label: 'Detection Rule',
    value: 'detection_rule',
  });
};

// Nosey Parker style code rendering - matches the terminal aesthetic
const renderNoseyParkerCodeLines = (
  snippet: { before: string; matching: string; after: string },
  startLine: number
) => {
  if (!snippet) return null;

  const beforeLines = snippet.before ? snippet.before.split('\n') : [];
  const matchingLines = snippet.matching ? snippet.matching.split('\n') : [];
  const afterLines = snippet.after ? snippet.after.split('\n') : [];

  const allLines = [...beforeLines, ...matchingLines, ...afterLines];
  const secretStartIndex = beforeLines.length;
  const secretEndIndex = secretStartIndex + matchingLines.length - 1;

  return allLines.map((line, index) => {
    const lineNumber = startLine - beforeLines.length + index;
    const isSecretLine = index >= secretStartIndex && index <= secretEndIndex;

    return (
      <tr key={index}>
        <td
          className="text-chariot-text-tertiary border-chariot-stroke border-r px-2 py-1 text-xs select-none"
          style={{ width: '40px' }}
        >
          {lineNumber > 0 ? lineNumber : ''}
        </td>
        <td className="px-3 py-1 text-sm">
          {isSecretLine ? (
            <div className="group flex items-center space-x-2">
              <CopyToClipboard alwaysShowCopyIcon textToCopy={line}>
                <code className="border-chariot-stroke text-chariot-text-red rounded border px-1 font-bold">
                  {line}
                </code>
              </CopyToClipboard>
            </div>
          ) : (
            <code className="text-chariot-text-tertiary">{line || ' '}</code>
          )}
        </td>
      </tr>
    );
  });
};

export const processSecretsDataSource = (
  file: POEContent | string,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>
) => {
  const secretsData = safeExecute(() => {
    return typeof file === 'string' ? JSON.parse(file) : file;
  });

  // Normalize to unified format
  const findings = normalizeSecretsFindings(secretsData);
  if (!findings || findings.length === 0) return;

  // Helper function to truncate file paths/resource IDs in the middle
  const truncateLocation = (
    location: string,
    maxLength: number = 60
  ): string => {
    if (location.length <= maxLength) return location;

    // For ARNs (AWS resource IDs), show last part
    if (location.includes('arn:')) {
      const parts = location.split(':');
      if (parts.length > 3) {
        return `...${parts.slice(-2).join(':')}`;
      }
    }

    // For file paths
    const pathParts = location.split('/');
    const fileName = pathParts[pathParts.length - 1];
    const startPath = pathParts.slice(0, 2).join('/');

    if (startPath.length + fileName.length + 4 >= maxLength) {
      return `${startPath}/.../${fileName}`;
    }

    let truncated = startPath;
    for (let i = 2; i < pathParts.length - 1; i++) {
      const testPath = `${truncated}/${pathParts[i]}`;
      if (testPath.length + fileName.length + 4 > maxLength) {
        break;
      }
      truncated = testPath;
    }

    return `${truncated}/.../${fileName}`;
  };

  // Group secrets by location (file path or resource ID)
  const secretsByLocation = findings.reduce(
    (acc, finding) => {
      const location = getLocationKey(finding);
      if (!acc[location]) {
        acc[location] = [];
      }
      acc[location].push(finding);
      return acc;
    },
    {} as Record<string, typeof findings>
  );

  sections['discovered_secrets'] = (
    <div className="space-y-6">
      {Object.entries(secretsByLocation).map(([location, matches]) => {
        const displayLocation = truncateLocation(location);

        return (
          <div key={location} className="space-y-3">
            <h4 className="text-chariot-text-primary pb-1 font-medium">
              <CopyToClipboard
                iconClassName="size-3 text-chariot-text-secondary"
                alwaysShowCopyIcon
                textToCopy={location}
              >
                {displayLocation}
              </CopyToClipboard>
            </h4>
            <div className="space-y-2 pl-4">
              {matches.map((finding, idx) => {
                const secret = finding.snippet.matching;

                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="bg-chariot-stroke flex size-2 items-center justify-center rounded-full" />
                    <CopyToClipboard
                      iconClassName="size-3 text-chariot-text-secondary"
                      alwaysShowCopyIcon
                    >
                      <span className="bg-chariot-background-secondary text-chariot-text-primary rounded-md px-2 py-1 font-mono text-sm">
                        {secret}
                      </span>
                    </CopyToClipboard>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  tempTabs['INFORMATION'].push({
    label: 'Discovered Secrets',
    value: 'discovered_secrets',
  });

  // Cloud Resource tab (only for cloud secrets with platform info)
  const cloudFindings = findings.filter(f => f.provenance.platform);
  if (cloudFindings.length > 0) {
    const firstFinding = cloudFindings[0];
    sections['cloud_resource'] = (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-2">
              {firstFinding.provenance.resource_type && (
                <div className="text-sm">
                  <span className="text-chariot-brand-primary font-medium">
                    Resource Type:
                  </span>
                  <span className="text-chariot-text-primary ml-2">
                    {firstFinding.provenance.resource_type}
                  </span>
                </div>
              )}
              {firstFinding.provenance.platform && (
                <div className="text-sm">
                  <span className="text-chariot-brand-primary font-medium">
                    Platform:
                  </span>
                  <span className="text-chariot-text-primary ml-2 uppercase">
                    {firstFinding.provenance.platform}
                  </span>
                </div>
              )}
              {firstFinding.provenance.region && (
                <div className="text-sm">
                  <span className="text-chariot-brand-primary font-medium">
                    Region:
                  </span>
                  <span className="text-chariot-text-primary ml-2">
                    {firstFinding.provenance.region}
                  </span>
                </div>
              )}
              {firstFinding.provenance.account_id && (
                <div className="text-sm">
                  <span className="text-chariot-brand-primary font-medium">
                    Account ID:
                  </span>
                  <span className="text-chariot-text-primary ml-2">
                    {firstFinding.provenance.account_id}
                  </span>
                </div>
              )}
            </div>
          </div>
          {firstFinding.provenance.resource_id && (
            <div className="flex items-center space-x-2 pt-2">
              <span className="text-chariot-brand-primary text-sm font-medium">
                Resource ID:
              </span>
              <CopyToClipboard
                alwaysShowCopyIcon
                textToCopy={firstFinding.provenance.resource_id}
              >
                <code className="text-chariot-text-primary flex-1 text-sm break-all">
                  {firstFinding.provenance.resource_id}
                </code>
              </CopyToClipboard>
            </div>
          )}
        </div>
      </div>
    );

    tempTabs['INFORMATION'].push({
      label: 'Cloud Resource',
      value: 'cloud_resource',
    });
  }

  // Recent Activity tab (only for Git repos with commits)
  const repoFindings = findings.filter(f => f.provenance.first_commit);
  if (repoFindings.length > 0) {
    const recentCommits = repoFindings
      .map(f => f.provenance.first_commit?.commit_metadata)
      .filter(Boolean)
      .sort(
        (a, b) =>
          new Date(b!.author_timestamp).getTime() -
          new Date(a!.author_timestamp).getTime()
      )
      .slice(0, 3);

    if (recentCommits.length > 0) {
      sections['recent_activity'] = (
        <div className="relative">
          {/* Timeline line */}
          <div className="bg-chariot-stroke-secondary absolute top-6 bottom-0 left-3 w-px"></div>

          <div className="space-y-4">
            {recentCommits.map((commit, idx) => {
              const message = commit!.message || 'No commit message';
              const maxLength = 80;
              const truncated =
                message.length > maxLength
                  ? `${message.substring(0, maxLength)}...`
                  : message;
              const isLast = idx === recentCommits.length - 1;

              return (
                <div key={idx} className="relative flex items-start space-x-4">
                  {/* Timeline node */}
                  <div className="relative z-10 flex size-6 items-center justify-center">
                    <div className="border-chariot-background-primary bg-chariot-stroke size-3 rounded-full border-2"></div>
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1 pb-4">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        {message.length > maxLength ? (
                          <Tooltip
                            title={
                              <div className="max-w-md whitespace-pre-wrap">
                                {message}
                              </div>
                            }
                          >
                            <div className="text-chariot-text-primary cursor-help text-sm leading-5 font-medium">
                              {truncated}
                            </div>
                          </Tooltip>
                        ) : (
                          <div className="text-chariot-text-primary text-sm leading-5 font-medium">
                            {message}
                          </div>
                        )}
                        <div className="text-chariot-text-tertiary mt-1 text-xs">
                          <span className="font-medium">
                            {commit!.author_name}
                          </span>
                          <span className="mx-1">•</span>
                          <span>
                            {formatTimestamp(commit!.author_timestamp)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3 shrink-0">
                        <CopyToClipboard
                          iconClassName="size-3 text-chariot-text-secondary"
                          alwaysShowCopyIcon
                          textToCopy={commit!.commit_id || 'unknown'}
                        >
                          <code className="border-chariot-stroke text-chariot-text-secondary rounded border px-2 py-1 font-mono text-xs whitespace-nowrap">
                            {commit!.commit_id?.substring(0, 7) || 'unknown'}
                          </code>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </div>

                  {/* Hide line for last item */}
                  {isLast && (
                    <div className="bg-chariot-background-primary absolute top-6 bottom-0 left-3 w-px"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
      tempTabs['INFORMATION'].push({
        label: 'Recent Activity',
        value: 'recent_activity',
      });
    }
  }

  // Repository context (only for repo-based secrets)
  const repoUrl = (secretsData as SecretsPOE).repository_url;
  if (repoUrl) {
    sections['repository'] = (
      <div className="flex items-center space-x-2">
        <CopyToClipboard alwaysShowCopyIcon textToCopy={repoUrl}>
          <code className="text-chariot-text-primary flex-1 text-sm">
            {repoUrl}
          </code>
        </CopyToClipboard>
      </div>
    );
    tempTabs['INFORMATION'].push({
      label: 'Repository',
      value: 'repository',
    });
  }

  // Analysis notes (only for repo-based secrets)
  const comment = (secretsData as SecretsPOE).comment;
  if (comment) {
    sections['analysis_notes'] = (
      <div className="text-chariot-text-primary text-sm">{comment}</div>
    );
    tempTabs['INFORMATION'].push({
      label: 'Analysis Notes',
      value: 'analysis_notes',
    });
  }

  // Rule information (for all secrets)
  addDetectionRuleSection(
    findings[0]?.rule_name,
    findings[0]?.rule_text_id,
    sections,
    tempTabs
  );

  // Validation tab (when validation data is available from secrets scanning)
  const validation = findings[0]?.validation;
  if (validation) {
    const statusColor =
      validation.status === 'valid'
        ? 'text-chariot-stroke-info'
        : validation.status === 'invalid'
          ? 'text-chariot-stroke-critical'
          : 'text-chariot-text-secondary';

    const StatusIcon =
      validation.status === 'valid'
        ? Check
        : validation.status === 'invalid'
          ? X
          : InformationCircleIcon;

    sections['validation'] = (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-chariot-brand-primary font-medium">
            Status:
          </span>
          <span
            className={`flex items-center gap-1.5 font-medium ${statusColor}`}
          >
            <StatusIcon className="size-4" />
            {validation.status.charAt(0).toUpperCase() +
              validation.status.slice(1)}
          </span>
        </div>
        {validation.confidence != null && (
          <div className="text-sm">
            <span className="text-chariot-brand-primary font-medium">
              Confidence:
            </span>
            <span className="text-chariot-text-primary ml-2">
              {Math.round(validation.confidence * 100)}%
            </span>
          </div>
        )}
        {validation.message && (
          <div className="text-sm">
            <span className="text-chariot-brand-primary font-medium">
              Details:
            </span>
            <span className="text-chariot-text-primary ml-2">
              {validation.message}
            </span>
          </div>
        )}
      </div>
    );

    tempTabs['INFORMATION'].push({
      label: 'Validation',
      value: 'validation',
    });
  }

  // Individual finding tabs - Unified for both repo and cloud secrets
  findings.forEach((finding, index) => {
    const displayName = getSecretDisplayName(finding);
    const sectionKey = `secrets_${displayName.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${index}`;
    const locationDisplay = getLocationDisplay(finding);
    const isCloudSecret = !!finding.provenance.platform;
    const isRepoSecret = !!finding.provenance.first_commit && !isCloudSecret;

    sections[sectionKey] = (
      <div className="space-y-4">
        <div className="border-chariot-stroke overflow-hidden rounded-lg border">
          {/* Finding header */}
          <div className="border-chariot-stroke bg-chariot-background-primary border-b px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center space-x-3">
                  <span className="text-chariot-brand-primary font-mono text-sm">
                    {locationDisplay}
                  </span>
                  {isRepoSecret && finding.location?.source_span && (
                    <span className="text-chariot-text-tertiary text-sm">
                      bytes {finding.location.source_span.start.line || 0}-
                      {finding.location.source_span.end.line || 0}
                    </span>
                  )}
                  {isCloudSecret && finding.provenance.resource_type && (
                    <span className="text-chariot-text-tertiary text-sm">
                      {finding.provenance.resource_type}
                    </span>
                  )}
                </div>
                {isRepoSecret && finding.provenance.first_commit?.blob_path && (
                  <div className="text-chariot-text-tertiary font-mono text-xs">
                    {finding.provenance.first_commit.blob_path}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Provenance info - Git or Cloud */}
          {(isRepoSecret || isCloudSecret) && (
            <div className="border-chariot-stroke border-b px-4 py-3">
              <div className="space-y-2">
                {isCloudSecret && (
                  <>
                    {finding.provenance.platform && (
                      <div className="text-sm">
                        <span className="text-chariot-brand-primary">
                          Platform:
                        </span>
                        <span className="text-chariot-text-primary ml-2 uppercase">
                          {finding.provenance.platform}
                        </span>
                      </div>
                    )}
                    {finding.provenance.resource_id && (
                      <div className="text-sm">
                        <span className="text-chariot-brand-primary">
                          Resource:
                        </span>
                        <span className="text-chariot-text-primary ml-2 font-mono text-xs break-all">
                          {finding.provenance.resource_id}
                        </span>
                      </div>
                    )}
                    {finding.provenance.region && (
                      <div className="text-sm">
                        <span className="text-chariot-brand-primary">
                          Region:
                        </span>
                        <span className="text-chariot-text-primary ml-2">
                          {finding.provenance.region}
                        </span>
                      </div>
                    )}
                    {finding.provenance.first_commit?.blob_path && (
                      <div className="text-sm">
                        <span className="text-chariot-brand-primary">
                          Location:
                        </span>
                        <span className="text-chariot-text-primary ml-2">
                          {finding.provenance.first_commit.blob_path}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {isRepoSecret &&
                  finding.provenance.first_commit?.commit_metadata && (
                    <>
                      <div className="text-sm">
                        <span className="text-chariot-brand-primary">
                          First commit:
                        </span>
                        <span className="text-chariot-text-primary ml-2 font-mono">
                          {finding.provenance.first_commit.commit_metadata.commit_id.substring(
                            0,
                            8
                          )}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-chariot-brand-primary">
                          Author:
                        </span>
                        <span className="text-chariot-text-primary ml-2">
                          {finding.provenance.first_commit.commit_metadata
                            .author_name || 'Unknown'}{' '}
                          {finding.provenance.first_commit.commit_metadata
                            .author_email
                            ? `<${finding.provenance.first_commit.commit_metadata.author_email}>`
                            : ''}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-chariot-brand-primary">
                          Date:
                        </span>
                        <span className="text-chariot-text-primary ml-2">
                          {formatTimestamp(
                            finding.provenance.first_commit.commit_metadata
                              .author_timestamp
                          ) || 'Unknown'}
                        </span>
                      </div>
                      {finding.provenance.first_commit.commit_metadata
                        .message && (
                        <div className="text-sm">
                          <span className="text-chariot-brand-primary">
                            Summary:
                          </span>
                          {(() => {
                            const message =
                              finding.provenance.first_commit.commit_metadata
                                .message || 'No commit message';
                            const maxLength = 60;
                            const truncated =
                              message.length > maxLength
                                ? `${message.substring(0, maxLength)}...`
                                : message;

                            return message.length > maxLength ? (
                              <Tooltip
                                title={
                                  <div className="max-w-md whitespace-pre-wrap">
                                    {message}
                                  </div>
                                }
                              >
                                <span className="text-chariot-text-primary ml-2 cursor-help">
                                  {truncated}
                                </span>
                              </Tooltip>
                            ) : (
                              <span className="text-chariot-text-primary ml-2">
                                {message}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </>
                  )}
              </div>
            </div>
          )}

          {/* Code snippet */}
          <div className="px-4 py-3">
            <div className="mb-3 text-sm">
              <span className="text-chariot-brand-primary">Snippet:</span>
            </div>
            <div className="bg-chariot-background-primary overflow-hidden rounded">
              <div className="font-mono text-sm">
                {isRepoSecret && finding.location?.source_span ? (
                  <table className="w-full">
                    <tbody>
                      {renderNoseyParkerCodeLines(
                        finding.snippet,
                        finding.location.source_span.start.line
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-2">
                    <div className="text-chariot-text-tertiary">
                      {finding.snippet.before}
                    </div>
                    <div className="text-chariot-text-primary bg-chariot-status-medium my-1 inline-block rounded px-1">
                      {finding.snippet.matching}
                    </div>
                    <div className="text-chariot-text-tertiary">
                      {finding.snippet.after}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    tempTabs['DETECTION'].push({
      label: displayName,
      value: sectionKey,
    });
  });
};
